import React, { useState } from "react";
import "./ResetPassword.css";
import { Form, Layout, Row, Col, Input, Button, message } from "antd";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";

import STUDENT_UPDATE_PASSWORD from "graphql/mutations/StudentUpdatePassword";
import useErrors from "hooks/useErrors";
import ErrorAlerting from "common/error_alerting";
import { PageContent } from "common/layout";
import { H1 } from "common/text";

const { Content } = Layout;

const SuccessPanel = styled.div`
  margin-top: 40px;
  margin-bottom: 20px;
`;

function ResetPasswordConfirm() {
  const location = useLocation();
  const { errors, handleError, closeError } = useErrors();
  // NOTE: URLSearchParams works in Edge browser but not legacy IE browsers
  const params = new URLSearchParams(location?.search);
  const resetPasswordToken = params.get("reset_password_token");

  const { t: loginTranslator } = useTranslation("login");
  const { t: regTranslator } = useTranslation("registration");
  const { t: errorTranslator } = useTranslation("error");
  const [passwordChanged, setPasswordChanged] = useState(false);

  const [updatePassword] = useMutation(STUDENT_UPDATE_PASSWORD);

  const handleSubmitChangePasswordForm = async (values: any) => {
    try {
      await updatePassword({ variables: values });
      setPasswordChanged(true);
      message.success(loginTranslator("Password was changed successfully!"));
    } catch (e) {
      handleError(e);
    }
  };

  if (!resetPasswordToken) {
    return (
      <PageContent style={{ backgroundColor: "white" }}>
        <H1 style={{ marginTop: 50 }}>
          {errorTranslator("Sorry, the link is not valid.")}
        </H1>
      </PageContent>
    );
  }

  return (
    <Content className="content-container login-container">
      <ErrorAlerting errors={errors} onClose={closeError} />
      <div className="login-card">
        <div className="card-title">
          {loginTranslator("Set New Password").toLocaleUpperCase()}
        </div>
        {passwordChanged ? (
          <SuccessPanel>
            {loginTranslator("Password was changed successfully!")}
            <div>
              <Link to="/login">
                {loginTranslator("Login").toLocaleUpperCase()}
              </Link>
            </div>
          </SuccessPanel>
        ) : (
          <Form name="updatePassword" onFinish={handleSubmitChangePasswordForm}>
            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: regTranslator("Password is required."),
                },
                {
                  min: 6,
                  message: regTranslator(
                    "The password must have a minimun length of 6 characters."
                  ),
                },
              ]}
            >
              <Input
                className="input"
                type="password"
                placeholder={loginTranslator("Enter your new password")}
                name="email"
                size="large"
              />
            </Form.Item>
            <Form.Item
              name="passwordConfirmation"
              rules={[
                {
                  required: true,
                  message: regTranslator("Password confirmation is required."),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        regTranslator(
                          "The two passwords that you entered do not match!"
                        )
                      )
                    );
                  },
                }),
              ]}
            >
              <Input
                className="input"
                type="password"
                placeholder={loginTranslator("Confirm your new password")}
                name="email"
                size="large"
              />
            </Form.Item>
            <Form.Item
              hidden
              name="resetPasswordToken"
              initialValue={resetPasswordToken}
            >
              <Input type="hidden" name="resetPasswordToken" />
            </Form.Item>
            <Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Button
                    className="log-in-button"
                    type="primary"
                    htmlType="submit"
                    disabled={passwordChanged}
                  >
                    {loginTranslator("Submit").toLocaleUpperCase()}
                  </Button>
                </Col>
              </Row>
            </Form.Item>
          </Form>
        )}
      </div>
    </Content>
  );
}

export default ResetPasswordConfirm;
